/*
 *  -----------------------------------------------------------------------
 *    <copyright file="site-dashboard/review-section.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'hooks';
import Constants from 'utils/constants';
import ItemWrapper from 'views/components/core/item-wrapper';
import 'views/components/central-monitoring/site-dashboard-summary/styles.scss';

const previousScore = (siteScoreInfo: any) =>
    siteScoreInfo?.previousScores?.length
        ? siteScoreInfo.previousScores[siteScoreInfo.previousScores.length - 1].score
        : null;

const siteScoreIcon = (siteScoreInfo: any, previousScoreInfo: any) =>
    previousScoreInfo
        ? parseFloat(previousScoreInfo.toFixed(2)) > parseFloat(siteScoreInfo?.score?.toFixed(2))
            ? 'arrow-circle-down'
            : parseFloat(previousScoreInfo.toFixed(2)) < parseFloat(siteScoreInfo?.score?.toFixed(2))
            ? 'arrow-circle-up'
            : 'minus-circle'
        : 'minus-circle';

const siteScoreColor = (siteScoreInfo: any) =>
    siteScoreInfo?.color === Constants.SiteColors.RED
        ? 'red'
        : siteScoreInfo?.color === Constants.SiteColors.AMBER
        ? 'amber'
        : siteScoreInfo?.color === Constants.SiteColors.GREEN
        ? 'green'
        : 'black';

const siteScore = (siteScoreInfo: any) => siteScoreInfo?.score.toFixed(2);

const ReviewSection = ({ datasetDate, onOpenEditReview, canEdit, siteReview, studyReviewInfo, siteScoreInfo }: any) => {
    const { t } = useTranslation();
    const previousScoreInfo = previousScore(siteScoreInfo);
    const scoreIcon = siteScoreIcon(siteScoreInfo, previousScoreInfo);
    const scoreColour = siteScoreColor(siteScoreInfo);
    const score = siteScore(siteScoreInfo);

    return (
        <span className="site-summary-row row">
            <ItemWrapper
                className="summary"
                title={t('cm:review')}
                hideItemNumber
                actions={
                    datasetDate?.isDataset && datasetDate?.id
                        ? [
                              {
                                  label: 'cm:editReview',
                                  icon: ['fal', 'pen-to-square'],
                                  onClick: () => {
                                      onOpenEditReview(siteReview);
                                  },
                                  enabled: canEdit,
                              },
                          ]
                        : null
                }
            >
                <div className="site-summary-flex-wrap review">
                    <span className="site-summary-text-item-wrap">
                        <p className="title">{t('cm:reviewed')}:</p>
                        {siteReview?.reviewed ? t('common:yes') : t('common:no')}
                    </span>
                    <span className="site-summary-text-item-wrap">
                        <p className="title">{t('reports:reviewer')}:</p>
                        {siteReview?.reviewer}
                    </span>
                    <span className="site-summary-text-item-wrap">
                        <p className="title">{t('cm:reviewStartDate')}:</p>
                        {studyReviewInfo?.reviewStartDate}
                    </span>
                    <span className="site-summary-text-item-wrap">
                        <p className="title">{t('cm:reviewCompleteDate')}:</p>
                        {studyReviewInfo?.reviewCompleteDate}
                    </span>
                </div>
                <span className="review-summary">
                    <p className="title">{t('cm:reviewSummary')}:</p>
                    {siteReview?.summary ?? siteReview?.reviewSummary}
                </span>
            </ItemWrapper>
            <ItemWrapper className="site-score" title={t('cm:siteScore')}>
                <div className="site-summary-flex-wrap review">
                    <span className="site-summary-text-item-wrap-site-score">
                        <FontAwesomeIcon icon={['far', scoreIcon]} className={`arrow-circle ${scoreColour}`} />
                    </span>
                    <span className="site-summary-text-item-wrap-site-score">
                        <p className="site-score-value">{score}</p>
                    </span>
                </div>
            </ItemWrapper>
        </span>
    );
};

export default ReviewSection;
